import React, { useEffect, useRef, useState } from "react";
import {
  Mail, Loader2, Plus, Trash2, Send, Users, CheckCircle2, AlertTriangle, Copy, Check, ImageIcon, Upload,
} from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { toast } from "sonner";

function timeAgo(iso) {
  try {
    const sec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (sec < 60) return "just now";
    if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
    if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
    return new Date(iso).toLocaleDateString([], { month: "short", day: "numeric" });
  } catch { return ""; }
}

export default function Newsletter() {
  const [overview, setOverview] = useState(null);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const logoInputRef = useRef(null);
  const [otherStations, setOtherStations] = useState([]);
  const [importFrom, setImportFrom] = useState("");
  const [importing, setImporting] = useState(false);
  const [syncSources, setSyncSources] = useState([]);
  const [syncBusy, setSyncBusy] = useState("");

  const load = async () => {
    try {
      const [a, b, c, d] = await Promise.all([
        api.get("/newsletter/overview"),
        api.get("/newsletter/subscribers"),
        api.get("/newsletter/other-stations"),
        api.get("/newsletter/sync-links"),
      ]);
      setOverview(a.data);
      setSubs(b.data || []);
      setOtherStations(c.data.stations || []);
      setSyncSources(d.data.sources || []);
    } catch { toast.error("Failed to load newsletter data"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const addSub = async () => {
    if (!newEmail.trim()) { toast.error("Enter an email"); return; }
    setAdding(true);
    try {
      await api.post("/newsletter/subscribers", { email: newEmail, name: newName });
      setNewEmail(""); setNewName("");
      toast.success("Subscriber added");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
    finally { setAdding(false); }
  };

  const removeSub = async (id) => {
    try {
      await api.delete(`/newsletter/subscribers/${id}`);
      setSubs((arr) => arr.filter((s) => s.subscriber_id !== id));
      toast.success("Subscriber removed");
    } catch { toast.error("Remove failed"); }
  };

  const sendNow = async () => {
    if (!content.trim()) { toast.error("Write the newsletter content first"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/newsletter/send", { subject, content });
      toast.success(`Sent to ${data.sent} of ${data.total} subscriber${data.total === 1 ? "" : "s"}${data.failed ? ` · ${data.failed} failed` : ""}`, { duration: 8000 });
      setSubject(""); setContent("");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed", { duration: 8000 }); }
    finally { setSending(false); }
  };

  const importStation = async () => {
    if (!importFrom) { toast.error("Pick a station to import from"); return; }
    setImporting(true);
    try {
      const { data } = await api.post("/newsletter/subscribers/import-station", { source_station_id: importFrom });
      toast.success(`Imported ${data.imported} subscriber${data.imported === 1 ? "" : "s"}${data.skipped ? ` · ${data.skipped} already on this list` : ""}`, { duration: 8000 });
      setImportFrom("");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    finally { setImporting(false); }
  };

  const toggleSync = async (stationId, enabled) => {
    setSyncBusy(stationId);
    try {
      await api.post("/newsletter/sync-links", { source_station_id: stationId, enabled });
      setSyncSources((arr) => enabled ? [...arr, stationId] : arr.filter((s) => s !== stationId));
      const name = otherStations.find((s) => s.station_id === stationId)?.name || "that station";
      toast.success(enabled
        ? `Auto-sync on — new ${name} signups now land on this list too`
        : `Auto-sync from ${name} turned off`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update auto-sync"); }
    finally { setSyncBusy(""); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setLogoBusy(true);
    try {
      const fd = new FormData();
      fd.append("logo", file);
      await api.post("/newsletter/logo", fd);
      toast.success("Station logo saved — it now heads every newsletter");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Logo upload failed"); }
    finally { setLogoBusy(false); if (logoInputRef.current) logoInputRef.current.value = ""; }
  };

  const removeLogo = async () => {
    setLogoBusy(true);
    try {
      await api.delete("/newsletter/logo");
      toast.success("Reverted to the BroadcastFlow AI logo");
      await load();
    } catch { toast.error("Could not remove logo"); }
    finally { setLogoBusy(false); }
  };

  const copySnippet = () => {
    const snippet = `<form onsubmit="event.preventDefault();fetch('${overview?.public_subscribe_url}',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({station_id:'${overview?.station_id}',email:this.email.value})}).then(function(){alert('Subscribed!')})">\n  <input name="email" type="email" placeholder="Your email" required />\n  <button type="submit">Subscribe</button>\n</form>`;
    navigator.clipboard.writeText(snippet).then(
      () => { setCopied(true); setTimeout(() => setCopied(false), 1600); toast.success("Embed snippet copied"); },
      () => toast.error("Clipboard blocked"),
    );
  };

  const subscribedCount = overview?.subscribed ?? 0;

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto animate-fade-up" data-testid="newsletter-page">
      <PageHeader
        eyebrow="// LISTENER NEWSLETTER"
        title="Land in your listeners' inbox."
        description="A channel you fully own. Manual blasts send instantly — and any Auto-poster or scheduled post with the Email channel toggled on delivers here too."
      />

      {loading ? (
        <div className="mt-8 flex items-center gap-2 text-ink-muted text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : (
        <>
          {/* Stats row */}
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="newsletter-stats">
            <div className="rounded-xl border border-line bg-bg-surface p-4">
              <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-1">Subscribers</div>
              <div className="font-display text-2xl font-bold text-ink-primary" data-testid="newsletter-sub-count">{subscribedCount}</div>
            </div>
            <div className="rounded-xl border border-line bg-bg-surface p-4">
              <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-1">Emails sent</div>
              <div className="font-display text-2xl font-bold text-ink-primary">{overview?.total_emails_sent ?? 0}</div>
            </div>
            <div className="rounded-xl border border-line bg-bg-surface p-4">
              <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-1">Unsubscribed</div>
              <div className="font-display text-2xl font-bold text-ink-muted">{overview?.unsubscribed ?? 0}</div>
            </div>
            <div className="rounded-xl border border-line bg-bg-surface p-4">
              <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-1">Sender</div>
              {overview?.configured ? (
                <div className="flex items-center gap-1.5 text-emerald-300 text-xs mt-1" data-testid="newsletter-sender-ok">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  <span className="truncate" title={overview.from_email}>{overview.from_email}</span>
                </div>
              ) : (
                <div className="flex items-center gap-1.5 text-amber-300 text-xs mt-1" data-testid="newsletter-sender-missing">
                  <AlertTriangle className="h-3.5 w-3.5" /> Not configured
                </div>
              )}
            </div>
          </div>

          <div className="mt-6 grid lg:grid-cols-2 gap-6">
            {/* Compose */}
            <section className="rounded-xl border border-line bg-bg-surface p-6" data-testid="newsletter-compose">
              <div className="font-mono type-status-badge uppercase tracking-[0.2em] text-brand-amber/80 mb-1">// COMPOSE</div>
              <h3 className="type-section-heading text-ink-primary mb-4">Send a blast now</h3>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-ink-secondary">Subject</Label>
                  <Input value={subject} onChange={(e) => setSubject(e.target.value)}
                    placeholder="e.g. This week on the station…"
                    className="mt-1 bg-bg border-line" data-testid="newsletter-subject" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-ink-secondary">Content</Label>
                  <Textarea rows={7} value={content} onChange={(e) => setContent(e.target.value)}
                    placeholder="What's happening at the station? New shows, contests, this week's top spins…"
                    className="mt-1 bg-bg border-line" data-testid="newsletter-content" />
                </div>
                <button onClick={sendNow} disabled={sending || subscribedCount === 0}
                  data-testid="newsletter-send-btn"
                  className="inline-flex items-center gap-2 rounded-md bg-brand-amber px-5 py-2.5 type-button-label text-black amber-glow disabled:opacity-50">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  {sending ? "Sending…" : `Send to ${subscribedCount} subscriber${subscribedCount === 1 ? "" : "s"}`}
                </button>
                {subscribedCount === 0 && (
                  <p className="type-body text-ink-muted">Add at least one subscriber to send.</p>
                )}
              </div>

              {/* Branding */}
              <div className="mt-6 border-t border-line pt-4" data-testid="newsletter-branding">
                <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-2">Email header logo</div>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="h-14 w-36 rounded-md border border-line bg-bg-elevated/60 flex items-center justify-center overflow-hidden p-1.5">
                    {overview?.logo_url ? (
                      <img src={`${overview.logo_url}${overview.custom_logo ? `?v=${Date.now()}` : ""}`} alt="Newsletter logo"
                        className="max-h-full max-w-full object-contain" data-testid="newsletter-logo-preview" />
                    ) : <ImageIcon className="h-5 w-5 text-ink-muted" />}
                  </div>
                  <div className="flex items-center gap-2">
                    <input ref={logoInputRef} type="file" accept="image/*" className="hidden"
                      onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="newsletter-logo-input" />
                    <button onClick={() => logoInputRef.current?.click()} disabled={logoBusy}
                      data-testid="newsletter-logo-upload"
                      className="inline-flex items-center gap-1.5 rounded-md border border-line bg-bg-elevated px-3 py-1.5 text-[11px] text-ink-secondary hover:text-ink-primary disabled:opacity-50 transition">
                      {logoBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                      {overview?.custom_logo ? "Replace logo" : "Upload your logo"}
                    </button>
                    {overview?.custom_logo && (
                      <button onClick={removeLogo} disabled={logoBusy} data-testid="newsletter-logo-remove"
                        className="inline-flex items-center gap-1 rounded-md border border-line bg-bg-elevated px-2.5 py-1.5 text-[11px] text-ink-muted hover:text-rose-300 disabled:opacity-50 transition">
                        <Trash2 className="h-3 w-3" /> Remove
                      </button>
                    )}
                  </div>
                </div>
                <p className="type-body text-ink-muted text-xs mt-2">
                  {overview?.custom_logo
                    ? "Your station logo heads every issue — BroadcastFlow AI stays in the footer."
                    : "Using the BroadcastFlow AI logo. Upload your station's logo to make it yours (PNG/JPG, up to 2 MB)."}
                </p>
              </div>

              {/* Recent sends */}
              {(overview?.recent_sends || []).length > 0 && (
                <div className="mt-6 border-t border-line pt-4" data-testid="newsletter-recent-sends">
                  <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-2">Recent sends</div>
                  <div className="space-y-1.5">
                    {overview.recent_sends.map((s) => (
                      <div key={s.send_id} className="flex items-center justify-between gap-2 rounded-md border border-line bg-bg-elevated/60 px-3 py-2 text-xs">
                        <div className="min-w-0">
                          <div className="text-ink-primary truncate">{s.subject}</div>
                          <div className="text-[10px] font-mono text-ink-muted">
                            {s.source === "scheduled_post" ? "auto-poster" : "manual"} · {timeAgo(s.created_at)}
                          </div>
                        </div>
                        <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-mono ${s.failed ? "border-amber-500/40 bg-amber-500/10 text-amber-300" : "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"}`}>
                          {s.sent}/{s.total} sent
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>

            {/* Subscribers */}
            <section className="rounded-xl border border-line bg-bg-surface p-6" data-testid="newsletter-subscribers">
              <div className="font-mono type-status-badge uppercase tracking-[0.2em] text-brand-amber/80 mb-1">// AUDIENCE</div>
              <h3 className="type-section-heading text-ink-primary mb-4 flex items-center gap-2"><Users className="h-4 w-4 text-brand-amber" /> Subscribers</h3>

              <div className="flex flex-wrap items-end gap-2 mb-4">
                <div className="flex-1 min-w-[180px]">
                  <Label className="text-xs uppercase tracking-wider text-ink-secondary">Email</Label>
                  <Input value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
                    placeholder="listener@example.com" className="mt-1 bg-bg border-line" data-testid="newsletter-add-email" />
                </div>
                <div className="w-36">
                  <Label className="text-xs uppercase tracking-wider text-ink-secondary">Name</Label>
                  <Input value={newName} onChange={(e) => setNewName(e.target.value)}
                    placeholder="Optional" className="mt-1 bg-bg border-line" data-testid="newsletter-add-name" />
                </div>
                <button onClick={addSub} disabled={adding} data-testid="newsletter-add-btn"
                  className="inline-flex items-center gap-1.5 rounded-md border border-brand-amber/40 bg-brand-amber/10 px-3 py-2 text-xs font-semibold text-brand-amber hover:bg-brand-amber/20 disabled:opacity-50 transition">
                  {adding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Add
                </button>
              </div>

              {subs.length === 0 ? (
                <div className="rounded-md border border-dashed border-line bg-bg-elevated/40 p-6 text-center type-body text-ink-muted" data-testid="newsletter-empty">
                  <Mail className="h-6 w-6 text-brand-amber mx-auto mb-2" />
                  No subscribers yet — add your first listener above.
                </div>
              ) : (
                <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1" data-testid="newsletter-sub-list">
                  {subs.map((sub) => (
                    <div key={sub.subscriber_id}
                      className={`flex items-center justify-between gap-2 rounded-md border border-line bg-bg-elevated/60 px-3 py-2 ${sub.status !== "subscribed" ? "opacity-50" : ""}`}
                      data-testid={`newsletter-sub-${sub.subscriber_id}`}>
                      <div className="min-w-0">
                        <div className="text-xs text-ink-primary truncate">{sub.email}</div>
                        <div className="text-[10px] font-mono text-ink-muted">
                          {sub.name ? `${sub.name} · ` : ""}{sub.status}{sub.source === "public" ? " · via widget" : sub.source === "auto_sync" ? " · auto-synced" : sub.source === "cross_station" ? " · imported" : ""}
                        </div>
                      </div>
                      <button onClick={() => removeSub(sub.subscriber_id)}
                        data-testid={`newsletter-sub-del-${sub.subscriber_id}`}
                        className="shrink-0 text-ink-muted hover:text-rose-300 transition" title="Remove subscriber">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Cross-station import */}
              {otherStations.length > 0 && (
                <div className="mt-5 border-t border-line pt-4" data-testid="newsletter-import-block">
                  <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-2">Import from another station</div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <select
                      value={importFrom}
                      onChange={(e) => setImportFrom(e.target.value)}
                      data-testid="newsletter-import-select"
                      className="flex-1 min-w-[180px] rounded-md border border-line bg-bg px-2.5 py-2 text-xs text-ink-primary focus:outline-none focus:border-brand-amber/60"
                    >
                      <option value="">Choose one of your stations…</option>
                      {otherStations.map((s) => (
                        <option key={s.station_id} value={s.station_id}>
                          {s.name} ({s.subscribed_count} subscriber{s.subscribed_count === 1 ? "" : "s"})
                        </option>
                      ))}
                    </select>
                    <button onClick={importStation} disabled={importing || !importFrom}
                      data-testid="newsletter-import-btn"
                      className="inline-flex items-center gap-1.5 rounded-md border border-brand-amber/40 bg-brand-amber/10 px-3 py-2 text-xs font-semibold text-brand-amber hover:bg-brand-amber/20 disabled:opacity-50 transition">
                      {importing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Users className="h-3.5 w-3.5" />}
                      Import
                    </button>
                  </div>
                  <p className="type-body text-ink-muted text-xs mt-1.5">
                    Copies that station's subscribers onto this list — duplicates are skipped automatically.
                  </p>
                  <div className="mt-3 space-y-1.5">
                    {otherStations.map((s) => {
                      const on = syncSources.includes(s.station_id);
                      return (
                        <div key={s.station_id}
                          className="flex items-center justify-between gap-3 rounded-md border border-line bg-bg-elevated/40 px-3 py-2"
                          data-testid={`newsletter-sync-row-${s.station_id}`}>
                          <div className="min-w-0">
                            <div className="text-xs text-ink-primary truncate">Auto-sync from {s.name}</div>
                            <div className="text-[10px] font-mono text-ink-muted">
                              {on ? "New signups there copy here automatically" : "Off — one-time imports only"}
                            </div>
                          </div>
                          <Switch checked={on} disabled={syncBusy === s.station_id}
                            onCheckedChange={(v) => toggleSync(s.station_id, v)}
                            data-testid={`newsletter-sync-toggle-${s.station_id}`} />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Public signup embed */}
              <div className="mt-5 border-t border-line pt-4">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">Signup form for your website</div>
                  <button onClick={copySnippet} data-testid="newsletter-copy-snippet"
                    className="inline-flex items-center gap-1 rounded-md border border-line bg-bg-elevated px-2 py-0.5 text-[10px] text-ink-secondary hover:text-cyan-300 transition">
                    {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                    {copied ? "Copied" : "Copy embed HTML"}
                  </button>
                </div>
                <p className="type-body text-ink-muted text-xs">
                  Paste the snippet on your station site — visitors subscribe straight into this list.
                </p>
              </div>
            </section>
          </div>
        </>
      )}
    </div>
  );
}
